"use client";
import { useEffect, useState } from 'react'
import Link from 'next/link';
import Image from 'next/image';


import { ArrowRight, Clock, Phone, MapPin, Building2, Hash, Target, Lightbulb, ShieldCheck, Truck, Headset } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { getCategories } from '@/lib/api'
import { siteConfig } from '@/data/siteConfig'
import type { Category } from '@/types'

const CATEGORY_IMAGES: Record<string, string> = {
  'tower-bolts': '/category-tower-bolts.webp',
  'cabinet-handles': '/category-cabinet-handles.webp',
  'hinges': '/category-hinges.webp',
}

export function AboutPage() {
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getCategories().then(setCategories).finally(() => setLoading(false))
  }, [])

  return (
    <>


      {/* LocalBusiness JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': ['LocalBusiness', 'Organization'],
          '@id': 'https://jamnagarmetals.com/#organization',
          name: 'Jamnagar Metals',
          legalName: siteConfig.legal.entityName,
          url: 'https://jamnagarmetals.com',
          logo: 'https://jamnagarmetals.com/jamnagar-matels-logo-horizontal.svg',
          description: siteConfig.description,
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Jamnagar',
            addressLocality: 'Jamnagar',
            addressRegion: 'Gujarat',
            postalCode: '361001',
            addressCountry: 'IN',
          },
          telephone: siteConfig.phone,
          email: siteConfig.email,
          vatID: siteConfig.legal.gst,
          openingHoursSpecification: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
            opens: '09:00',
            closes: '19:00',
          },
          priceRange: 'Contact for quote',
          currenciesAccepted: 'USD, EUR, GBP, AED, INR',
          paymentAccepted: 'Bank Transfer, Letter of Credit, D/P',
          areaServed: 'Worldwide',
        })
      }} />

      {/* Hero */}
      <div className="relative py-20 px-4 overflow-hidden" style={{ backgroundColor: '#1C1E24' }}>
        <Image src="/hero.webp" alt="Hero" fill priority className="object-cover opacity-15" />
        <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ backgroundColor: '#C79A5C' }} />
        <div className="relative max-w-3xl mx-auto text-center">
          <Badge className="mb-4 text-xs text-white border-white/30" style={{ backgroundColor: 'rgba(46,109,164,0.5)' }}>
            {siteConfig.natureOfBusiness} · {siteConfig.location}
          </Badge>
          <h1 className="text-4xl font-extrabold text-white mb-4 tracking-tight">About Jamnagar Metals</h1>
          <p className="text-lg text-white/70 max-w-xl mx-auto leading-relaxed">
            Precision hardware for Indian industry — manufactured with expertise, delivered with trust.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-16">
        {/* Brand Story */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="text-2xl font-bold mb-4 tracking-tight">Our Story</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Jamnagar Metals is a manufacturer and trader of precision industrial hardware, specialising in tower bolts, barrel bolts, flush bolts, and a comprehensive range of cabinet handles and architectural fittings.
              </p>
              <p>
                Based in Jamnagar, Gujarat — widely recognised as India's hardware manufacturing capital — we combine traditional craftsmanship with modern CNC precision engineering to produce hardware that meets or exceeds Bureau of Indian Standards (BIS) specifications.
              </p>
              <p>
                We serve architects, contractors, procurement teams, hardware distributors, and project managers across India. Our B2B-first approach means we offer competitive bulk pricing, GST invoice on all transactions, and dedicated technical support for specification queries.
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <div className="bg-muted/50 rounded-xl p-6 border border-border">
              <p className="text-3xl font-bold mb-1" style={{ color: '#C79A5C' }}>2</p>
              <p className="text-sm text-muted-foreground">Product categories</p>
            </div>
            <div className="bg-muted/50 rounded-xl p-6 border border-border">
              <p className="text-3xl font-bold mb-1" style={{ color: '#C79A5C' }}>12+</p>
              <p className="text-sm text-muted-foreground">Product lines with multiple SKU variants</p>
            </div>
            <div className="bg-muted/50 rounded-xl p-6 border border-border">
              <p className="text-3xl font-bold mb-1" style={{ color: '#C79A5C' }}>100%</p>
              <p className="text-sm text-muted-foreground">Quality Assured (BIS Standards)</p>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="relative h-80 lg:h-full min-h-[400px] rounded-2xl overflow-hidden">
            <Image
              src="/manufacturing.webp"
              alt="Manufacturing Facility"
              fill
              className="object-cover"
            />
            <div className="absolute inset-0 bg-black/10" />
          </div>
          <div className="space-y-8">
            <div className="bg-muted/30 p-8 rounded-2xl border border-border">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-xl" style={{ backgroundColor: 'rgba(199, 154, 92, 0.1)' }}>
                  <Target className="h-6 w-6" style={{ color: '#C79A5C' }} />
                </div>
                <h2 className="text-2xl font-bold tracking-tight">Our Mission</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                To engineer and deliver the highest quality industrial hardware by combining traditional manufacturing excellence with modern technological precision. We strive to be the most reliable B2B partner for businesses across India, ensuring timely delivery, competitive pricing, and uncompromising quality in every product.
              </p>
            </div>

            <div className="bg-muted/30 p-8 rounded-2xl border border-border">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-xl" style={{ backgroundColor: 'rgba(199, 154, 92, 0.1)' }}>
                  <Lightbulb className="h-6 w-6" style={{ color: '#C79A5C' }} />
                </div>
                <h2 className="text-2xl font-bold tracking-tight">Our Vision</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                To be recognised globally as the benchmark for Indian hardware manufacturing, driving industry standards forward while maintaining our commitment to sustainable practices and building long-lasting partnerships with clients worldwide.
              </p>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section>
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tight mb-4">Why Choose Us</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              We understand the unique challenges of B2B hardware procurement. Here's how we add value to your supply chain.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-muted/20 border-border hover:border-[#C79A5C]/50 transition-colors">
              <CardContent className="p-6">
                <ShieldCheck className="h-10 w-10 mb-4" style={{ color: '#C79A5C' }} />
                <h3 className="font-bold text-lg mb-2">Quality Assured</h3>
                <p className="text-sm text-muted-foreground">Every product meets or exceeds strict Bureau of Indian Standards (BIS) specifications.</p>
              </CardContent>
            </Card>
            <Card className="bg-muted/20 border-border hover:border-[#C79A5C]/50 transition-colors">
              <CardContent className="p-6">
                <Building2 className="h-10 w-10 mb-4" style={{ color: '#C79A5C' }} />
                <h3 className="font-bold text-lg mb-2">B2B Specialist</h3>
                <p className="text-sm text-muted-foreground">Competitive bulk pricing, seamless GST invoicing, and volume discounts available.</p>
              </CardContent>
            </Card>
            <Card className="bg-muted/20 border-border hover:border-[#C79A5C]/50 transition-colors">
              <CardContent className="p-6">
                <Truck className="h-10 w-10 mb-4" style={{ color: '#C79A5C' }} />
                <h3 className="font-bold text-lg mb-2">Pan-India Delivery</h3>
                <p className="text-sm text-muted-foreground">Efficient logistics network ensuring timely delivery from our Jamnagar facility.</p>
              </CardContent>
            </Card>
            <Card className="bg-muted/20 border-border hover:border-[#C79A5C]/50 transition-colors">
              <CardContent className="p-6">
                <Headset className="h-10 w-10 mb-4" style={{ color: '#C79A5C' }} />
                <h3 className="font-bold text-lg mb-2">Technical Support</h3>
                <p className="text-sm text-muted-foreground">Dedicated team available to answer specification queries and project requirements.</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Product Specialisation */}
        <section>
          <h2 className="text-2xl font-bold mb-6 tracking-tight">Product Specialisation</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {loading
              ? [1, 2].map((i) => <Skeleton key={i} className="h-40 rounded-xl" />)
              : categories.map((cat) => (
                <Link key={cat.slug} href={`/${cat.slug}`} className="group block">
                  <Card className="overflow-hidden hover:shadow-md transition-all p-0 gap-0">
                    <div className="relative h-48 overflow-hidden">
                      <Image
                        src={CATEGORY_IMAGES[cat.slug] ?? '/hero.webp'}
                        alt={cat.name}
                        fill
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 400px"
                        className="object-cover group-hover:scale-105 transition-transform duration-300"
                        quality={75}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      <div className="absolute bottom-3 left-3">
                        <h3 className="text-white font-bold">{cat.name}</h3>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <p className="text-xs text-muted-foreground line-clamp-2">{cat.description}</p>
                      <p className="text-xs mt-2 flex items-center gap-1" style={{ color: '#C79A5C' }}>
                        Browse products <ArrowRight className="h-3 w-3" />
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
          </div>
        </section>

        {/* Working Hours + Trust Signals */}
        <section className="relative rounded-3xl overflow-hidden border border-border bg-gradient-to-br from-muted/50 to-muted/10 p-8 md:p-12">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#C79A5C]/10 rounded-full blur-3xl -mr-20 -mt-20" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#C79A5C]/5 rounded-full blur-3xl -ml-20 -mb-20" />

          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-5 space-y-6">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C79A5C]/10 text-[#C79A5C] text-sm font-medium mb-4">
                  <Clock className="w-4 h-4" /> Working Hours
                </div>
                <h2 className="text-3xl font-bold tracking-tight mb-2">Always Ready to Serve</h2>
                <p className="text-muted-foreground">
                  Our team is dedicated to providing swift responses during business hours. Inquiries received outside these hours are prioritized on the next working day.
                </p>
              </div>
              <div className="bg-background/50 backdrop-blur-sm p-4 rounded-2xl border border-border inline-block">
                <p className="text-2xl font-bold text-foreground">{siteConfig.workingHours}</p>
              </div>
            </div>

            <div className="md:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { label: 'GST Invoice', sub: 'Available on all orders', icon: Building2 },
                  { label: 'Bulk Orders', sub: 'Competitive pricing', icon: Target },
                  { label: 'MOQ Policy', sub: 'Standard carton sizes', icon: Hash },
                  { label: 'IS Standard', sub: '100% Compliant', icon: ShieldCheck },
                ].map((item, i) => (
                  <div key={i} className="bg-background/50 backdrop-blur-sm p-5 rounded-2xl border border-border hover:border-[#C79A5C]/50 transition-colors group flex items-start gap-4">
                    <div className="bg-[#C79A5C]/10 p-3 rounded-xl group-hover:bg-[#C79A5C]/20 transition-colors">
                      <item.icon className="h-6 w-6 text-[#C79A5C]" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{item.label}</p>
                      <p className="text-xs text-muted-foreground mt-1">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-6 text-center italic">{siteConfig.moqNote}</p>
            </div>
          </div>
        </section>

        {/* Legal Disclosure */}
        <section className="relative overflow-hidden rounded-3xl border border-border/50 bg-[#1C1E24] p-1">
          <div className="absolute inset-0 bg-gradient-to-r from-[#C79A5C]/20 via-transparent to-[#C79A5C]/20 opacity-50" />
          <div className="relative bg-background rounded-[1.4rem] p-8 md:p-10">
            <div className="flex flex-col md:flex-row gap-8 justify-between items-start md:items-center border-b border-border/50 pb-8 mb-8">
              <div>
                <h2 className="text-2xl font-bold mb-2">Legal Entity Information</h2>
                <p className="text-muted-foreground">Official registration and compliance details</p>
              </div>
              <div className="p-3 bg-[#C79A5C]/10 rounded-2xl border border-[#C79A5C]/20">
                <ShieldCheck className="h-8 w-8 text-[#C79A5C]" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Building2 className="h-4 w-4" />
                  <span className="text-xs uppercase tracking-wider font-semibold">Registered Company</span>
                </div>
                <p className="text-lg font-medium text-foreground">{siteConfig.legal.entityName}</p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Hash className="h-4 w-4" />
                  <span className="text-xs uppercase tracking-wider font-semibold">GST Number</span>
                </div>
                <p className="text-lg font-medium font-mono text-foreground">{siteConfig.legal.gst}</p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span className="text-xs uppercase tracking-wider font-semibold">Registered Address</span>
                </div>
                <p className="text-sm text-foreground">{siteConfig.legal.registeredAddress}</p>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-border/50">
              <p className="text-sm text-muted-foreground/80 leading-relaxed max-w-3xl">
                {siteConfig.legal.brandStatement}
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative overflow-hidden rounded-3xl bg-[#1C1E24] text-white mt-16">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#C79A5C] rounded-full mix-blend-multiply filter blur-[120px] opacity-20" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-blue-500 rounded-full mix-blend-multiply filter blur-[120px] opacity-10" />
          <div className="absolute inset-0 bg-[url('/hero.webp')] bg-cover bg-center opacity-10 mix-blend-overlay" />

          <div className="relative px-6 py-16 md:py-24 flex flex-col items-center text-center">
            <h2 className="text-4xl md:text-5xl font-extrabold mb-6 tracking-tight">Ready to Elevate Your Supply Chain?</h2>
            <p className="text-lg text-white/70 mb-10 max-w-2xl">
              Partner with us for premium industrial hardware. Contact us today for bulk pricing, technical specifications, or to discuss your project requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <Link href="/quote" className="w-full sm:w-auto">
                <Button size="lg" className="w-full text-white font-bold text-md px-8 py-6 rounded-xl hover:scale-105 transition-transform" style={{ backgroundColor: '#C79A5C' }}>
                  Request a Quote <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link href="/contact" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="w-full bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white text-md px-8 py-6 rounded-xl transition-colors">
                  Contact Us
                </Button>
              </Link>
              <a href={`tel:${siteConfig.phone}`} className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="w-full bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white text-md px-8 py-6 rounded-xl transition-colors">
                  <Phone className="mr-2 h-5 w-5" /> Call Us
                </Button>
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}
